import {
  isCreditCustomerMailFailReason,
  isCreditCustomerMailSuppressReason,
  type CreditCustomerMailDeliveryRecord,
  type CreditCustomerMailReason,
} from '@nadoutong/contracts';
import { ROLE_OPS_ADMIN, hasAnyOpsRole } from '../../auth/opsRoles.ts';
import { useSession } from '../../hooks/useSession.tsx';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import {
  useCreateCreditCustomerMailSuppression,
  useCreditCustomerMailDeliveries,
  useCreditCustomerMailSuppressions,
  useDeleteCreditCustomerMailSuppression,
} from '../../api/creditCustomerMail.ts';
import { coerceErrorMessage, useToast } from '../Toast.tsx';
import { formatLocalTimestamp } from '../formatters.ts';
import {
  Button,
  Chip,
  EmptyState,
  Table,
  TableEmpty,
  TableSkeleton,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../../ui/index.ts';
import type { ChipTone } from '../../ui/index.ts';

const RESULT_TONE: Record<CreditCustomerMailDeliveryRecord['result'], ChipTone> = {
  sent: 'ok',
  suppressed: 'warn',
  failed: 'bad',
};

const RESULT_LABEL: Record<CreditCustomerMailDeliveryRecord['result'], I18nKey> = {
  sent: 'credit.mail.result.sent',
  suppressed: 'credit.mail.result.suppressed',
  failed: 'credit.mail.result.failed',
};

const REASON_LABEL: Record<CreditCustomerMailReason, I18nKey> = {
  unmapped_event: 'credit.mail.reason.unmapped_event',
  no_recipient: 'credit.mail.reason.no_recipient',
  reserved_invalid_recipient: 'credit.mail.reason.reserved_invalid_recipient',
  missing_due_date: 'credit.mail.reason.missing_due_date',
  zero_debt: 'credit.mail.reason.zero_debt',
  mail_disabled: 'credit.mail.reason.mail_disabled',
  account_muted: 'credit.mail.reason.account_muted',
  stale: 'credit.mail.reason.stale',
  superseded: 'credit.mail.reason.superseded',
  provider_unavailable: 'credit.mail.reason.provider_unavailable',
  render_failed: 'credit.mail.reason.render_failed',
  send_failed: 'credit.mail.reason.send_failed',
};

function mailReasonLabel(
  reason: string | null,
  t: (key: I18nKey) => string,
): string {
  if (reason === null || reason.length === 0) {
    return t('credit.overview.dash');
  }
  if (isCreditCustomerMailSuppressReason(reason) || isCreditCustomerMailFailReason(reason)) {
    return t(REASON_LABEL[reason]);
  }
  return reason;
}

interface Props {
  readonly orgId: string;
  readonly accountId: string;
}

export function CreditMailDeliveriesPanel({ orgId, accountId }: Props): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { user } = useSession();
  const isAdmin = hasAnyOpsRole(user?.roles ?? [], [ROLE_OPS_ADMIN]);
  const query = useCreditCustomerMailDeliveries(orgId, true);
  const suppressions = useCreditCustomerMailSuppressions(isAdmin);
  const mute = useCreateCreditCustomerMailSuppression();
  const unmute = useDeleteCreditCustomerMailSuppression();
  const entries = query.data?.entries ?? [];
  const muted = (suppressions.data?.suppressions ?? []).some(
    (row) => row.account_id === accountId,
  );

  return (
    <div className="flex flex-col gap-3" data-testid="customer-credit-mail">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="m-0 text-dense font-medium text-paper">
          {t('credit.mail.title')}
        </h3>
        {isAdmin ? (
          <Button
            size="sm"
            variant="ghost"
            loading={mute.isPending || unmute.isPending}
            onClick={() => {
              if (muted) {
                unmute.mutate(accountId, {
                  onSuccess: () => toast.success(t('credit.mail.unmute_toast')),
                  onError: (error) => toast.error(coerceErrorMessage(error)),
                });
                return;
              }
              mute.mutate(
                { accountId },
                {
                  onSuccess: () => toast.success(t('credit.mail.mute_toast')),
                  onError: (error) => toast.error(coerceErrorMessage(error)),
                },
              );
            }}
            data-testid="customer-credit-mail-mute"
          >
            {muted ? t('credit.mail.unmute') : t('credit.mail.mute')}
          </Button>
        ) : null}
      </div>
      {muted ? (
        <Chip tone="warn" testId="customer-credit-mail-muted">
          {t('credit.mail.muted_chip')}
        </Chip>
      ) : null}
      {query.isPending ? (
        <TableSkeleton rows={3} testId="customer-credit-mail-loading" />
      ) : null}
      {query.isError ? (
        <EmptyState
          title={t('credit.mail.load_error')}
          hint={coerceErrorMessage(query.error)}
          action={<Button onClick={() => void query.refetch()}>{t('common.retry')}</Button>}
          testId="customer-credit-mail-error"
        />
      ) : null}
      {query.data !== undefined ? (
        <Table testId="customer-credit-mail-table" className="rounded-none border-0">
          <THead>
            <Tr interactive={false}>
              <Th>{t('credit.mail.col.when')}</Th>
              <Th>{t('credit.mail.col.result')}</Th>
              <Th>{t('credit.mail.col.template')}</Th>
              <Th>{t('credit.mail.col.subject')}</Th>
              <Th>{t('credit.mail.col.to')}</Th>
              <Th>{t('credit.mail.col.reason')}</Th>
            </Tr>
          </THead>
          <TBody>
            {entries.length === 0 ? (
              <TableEmpty colSpan={6} testId="customer-credit-mail-empty">
                {t('credit.mail.empty')}
              </TableEmpty>
            ) : (
              entries.map((entry) => (
                <Tr key={entry.id} data-testid={`customer-credit-mail-row-${entry.id}`}>
                  <Td mono>{formatLocalTimestamp(entry.processed_at)}</Td>
                  <Td>
                    <Chip tone={RESULT_TONE[entry.result]}>
                      {t(RESULT_LABEL[entry.result])}
                    </Chip>
                  </Td>
                  <Td mono>{entry.template_key ?? '—'}</Td>
                  <Td>{entry.subject ?? '—'}</Td>
                  <Td mono>{entry.to_address ?? '—'}</Td>
                  <Td data-testid={`customer-credit-mail-reason-${entry.id}`}>
                    {mailReasonLabel(entry.reason, t)}
                  </Td>
                </Tr>
              ))
            )}
          </TBody>
        </Table>
      ) : null}
    </div>
  );
}
